// @ts-check
import React, { useState, useEffect } from 'react'
import { InputText } from '../../../ui'
import { LATIN_WITH_NUMBERS_SYMBOLS, MAX_LENGTH } from '../../../constants/constants'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import { map, isNil, isEmpty } from 'ramda'
import { usePrevious } from '../../utils/hooks'
import { getUserValue } from '../utils'

/**
 * @param {{
 * i18nLabel: string,
 * name: string,
 * dependOnBooleanFldName?: string,
 * additionalInfo?: string,
 * maxlength?: number,
 * required?: boolean,
 * order?: number
 * allowNonLatin: boolean
 * }} props
 */
const ExternalBooleanDependentTxt = ({
  i18nLabel,
  name,
  dependOnBooleanFldName,
  additionalInfo,
  maxlength,
  allowNonLatin
}) => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [shouldPresetValue, setShouldPresetValue] = useState(true)
  const [error, setError] = useState('')
  const [{ form, form: { accId }, user }, dispatch] = useStore()
  const dependOnBooleanFldValue = form[accId][dependOnBooleanFldName]
  const label = i18nLabel ? t(`generatedField.${i18nLabel}`) : ''
  const prevDependOnFieldValue = usePrevious(form[accId][dependOnBooleanFldName])
  const userValue = getUserValue(accId, user, name)
  const prevUserValue = usePrevious(userValue)

  useEffect(() => {
    if (prevDependOnFieldValue === dependOnBooleanFldValue && prevUserValue === userValue) {
      return
    }
    if (+dependOnBooleanFldValue === 1 || dependOnBooleanFldValue === 'Y') {
      if (userValue && shouldPresetValue) {
        presetValue(userValue)
        setIsVisible(true)
      } else {
        setIsVisible(true)
        dispatch({ type: 'addValidationFld', name })
        if (!isNil(value) && !isEmpty(value)) {
          presetValue(value)
        }
      }
    } else {
      dispatch({ type: 'removeValidationFld', name })
      dispatch({ type: 'removeFieldValue', name })
      setIsVisible(false)
    }
  }, [dependOnBooleanFldValue, userValue, shouldPresetValue, value, name])

  /**
 * @param {(string|number)} value
 */
  const presetValue = (value) => {
    setValue(value.toString())
    const isValid = typeof value === 'string' ? value.length > 0 : value > 0
    setIsValid(isValid)
    dispatch({
      type: 'setExtFieldValue',
      name,
      value: getValueByName(name, value)
    })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  const getValueByName = (name, value) => name === 'workedInFinancialSectorYears' ? +value : value

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onChange = ({ target: { value } }) => {
    setShouldPresetValue(false)
    if (name === 'workedInFinancialSectorYears') {
      const reg = /^\d+$/
      const isValidInput = (value.length && reg.test(value)) || !value.length
      if (isValidInput) {
        setValue(value)
      }
      return
    }
    setValue(value)
  }
  /**
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const onBlur = ({ currentTarget: { value, name } }) => {
    let isValid = value.length > 0
    if (name !== 'workedInFinancialSectorYears' && !allowNonLatin) {
      isValid = LATIN_WITH_NUMBERS_SYMBOLS.test(value.trim())
      setError(isValid ? '' : 'generatedField.onlyLatin')
    }
    dispatch({
      type: 'setExtFieldValue',
      name,
      value: getValueByName(name, value)
    })
    dispatch({ type: 'setValidationFld', name, value: isValid })
    setIsValid(isValid)
  }

  return isVisible ? (
    <InputText
      value={value}
      label={label}
      placeholder=''
      error={t(error)}
      name={name}
      onChange={onChange}
      onBlur={onBlur}
      maxLength={maxlength || MAX_LENGTH}
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
    />
  ) : null
}

const getExternalBooleanDependentTxtFields = map(textField => {
  const { i18n, name, required, order, maxLength, additionalInfo, dependOnBooleanFldName, allowNonLatin } = textField
  return (
    <ExternalBooleanDependentTxt
      i18nLabel={i18n}
      name={name}
      required={required}
      key={name}
      order={order}
      dependOnBooleanFldName={dependOnBooleanFldName}
      additionalInfo={additionalInfo}
      maxlength={maxLength}
      allowNonLatin={allowNonLatin}
    />
  )
})

export { getExternalBooleanDependentTxtFields, ExternalBooleanDependentTxt }
